import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  LinearProgress,
  Divider,
} from '@mui/material';
import {
  Security,
  Warning,
  CheckCircle,
  Error,
  Info,
  ExpandMore,
  TrendingUp,
  TrendingDown,
  Assessment,
  Visibility,
  Download,
  Refresh,
} from '@mui/icons-material';
import { useTranslation } from '../i18n';
import {
  AdvancedCreditScore,
  RiskFactor,
  FraudDetectionResult,
  RiskBasedPricing,
} from '../types';
import RiskBasedPricingService from '../services/riskBasedPricingService';
import FraudDetectionService from '../services/fraudDetectionService';
import riskManagementApi from '../services/riskManagementApi';

interface AdvancedRiskDashboardProps {
  application?: any; // Mock application data
}

const AdvancedRiskDashboard: React.FC<AdvancedRiskDashboardProps> = ({ application }) => {
  const { t } = useTranslation();
  const [expandedSection, setExpandedSection] = useState<string | false>(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [advancedScore, setAdvancedScore] = useState<AdvancedCreditScore | null>(null);
  const [fraudResult, setFraudResult] = useState<FraudDetectionResult | null>(null);
  const [pricingResult, setPricingResult] = useState<any>(null);
  const [portfolioRisk, setPortfolioRisk] = useState<any>(null);

  // Sample application for demo (in real app, this would come from props or context)
  const sampleApplication = {
    id: 'sample-app-001',
    applicantName: 'John Doe',
    email: 'john.doe@example.com',
    phone: '1234567890',
    nationalId: '1234567890123',
    monthlyIncome: 51000000,
    requestedAmount: 300000000,
    employmentStatus: 'employed',
    employmentDuration: 8,
    age: 35,
    existingLoans: 0,
    education: 'postgraduate',
    employerType: 'mnc',
    homeOwnership: 'owned',
    location: 'Mumbai',
    bankingHistory: 5,
    creditCards: 2,
    cibilScore: 785,
    status: 'approved',
    submissionDate: new Date('2023-10-01T10:00:00Z').toISOString(),
    creditScore: 820,
    interestRate: 8.5,
    customerType: 'individual',
    businessSector: 'technology'
  };

  // Fetch data from backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch advanced scoring
        const scoringResponse = await riskManagementApi.getAdvancedScoring(sampleApplication);
        setAdvancedScore(scoringResponse.advancedScore);
        setFraudResult(scoringResponse.fraudDetectionResult);

        // Fetch risk-based pricing
        const pricingResponse = await riskManagementApi.getRiskBasedPricing({
          creditScore: scoringResponse.advancedScore.finalScore,
          loanAmount: sampleApplication.requestedAmount,
          loanTerm: 36,
          customerType: sampleApplication.customerType as 'individual' | 'sme',
          sector: sampleApplication.businessSector
        });
        setPricingResult(pricingResponse.pricing);

        // Fetch portfolio risk summary
        const portfolioResponse = await riskManagementApi.getPortfolioRiskSummary();
        setPortfolioRisk(portfolioResponse.portfolioRisk);

      } catch (err) {
        console.error('Error fetching risk data:', err);
        setError('Failed to fetch risk data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6">Loading Advanced Risk Dashboard...</Typography>
        <LinearProgress sx={{ mt: 2 }} />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => window.location.reload()}
          startIcon={<Refresh />}
        >
          Retry
        </Button>
      </Box>
    );
  }

  // Use real data or fallback to mock data
  const currentAdvancedScore = advancedScore || {
    traditionalScore: 720,
    behavioralScore: 85,
    socialScore: 78,
    alternativeDataScore: 82,
    fraudRiskScore: 88,
    finalScore: 785,
    confidenceLevel: 92,
    riskFactors: [],
    recommendedActions: [],
    scoringModel: 'hybrid'
  };

  const currentFraudResult = fraudResult || {
    isFraudulent: false,
    fraudScore: 12,
    fraudIndicators: [],
    riskLevel: 'low',
    recommendedAction: 'approve',
    confidence: 92,
  };

  // Use real pricing data from backend
  const currentPricingResult = pricingResult || {
    baseRate: 8.0,
    riskAdjustment: 1.5,
    marketAdjustment: 0.0,
    finalRate: 9.5,
    pricingTiers: [],
    riskSegmentation: 'nearPrime'
  };

  // Mock pricing recommendations (in real app, this would come from backend)
  const pricingRecommendations = {
    recommendedRate: currentPricingResult.finalRate,
    alternativeRates: [
      { term: 12, rate: 8.5, monthlyPayment: 260000 },
      { term: 24, rate: 9.0, monthlyPayment: 137000 },
      { term: 36, rate: 9.5, monthlyPayment: 96000 },
      { term: 48, rate: 10.0, monthlyPayment: 76000 },
      { term: 60, rate: 10.5, monthlyPayment: 64000 }
    ],
    riskMitigation: [
      'Enhanced fraud monitoring required',
      'Additional identity verification needed'
    ]
  };

  const handleAccordionChange = (panel: string) => (
    event: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    setExpandedSection(isExpanded ? panel : false);
  };

  const getRiskColor = (level: string) => {
    switch (level) {
      case 'low': return 'success';
      case 'medium': return 'warning';
      case 'high': return 'error';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  const getFraudColor = (score: number) => {
    if (score >= 80) return 'error';
    if (score >= 60) return 'warning';
    if (score >= 30) return 'info';
    return 'success';
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 90) return 'success';
    if (confidence >= 70) return 'warning';
    return 'error';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        {t('advancedRisk.title')}
      </Typography>

      {/* Risk Overview Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Assessment color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">{t('advancedRisk.finalCreditScore')}</Typography>
            </Box>
            <Typography variant="h3" color="primary">
              {currentAdvancedScore.finalScore}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('advancedRisk.hybridScoringModel')}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={(currentAdvancedScore.finalScore / 900) * 100}
              sx={{ mt: 1 }}
            />
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Security color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">{t('advancedRisk.fraudRisk')}</Typography>
            </Box>
            <Typography variant="h3" color={getFraudColor(currentFraudResult.fraudScore) as any}>
              {currentFraudResult.fraudScore}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {currentFraudResult.riskLevel} {t('advancedRisk.riskLevel')}
            </Typography>
            <Chip
              label={currentFraudResult.recommendedAction}
              color={currentFraudResult.recommendedAction === 'approve' ? 'success' : 'warning'}
              size="small"
              sx={{ mt: 1 }}
            />
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <CheckCircle color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">{t('advancedRisk.confidence')}</Typography>
            </Box>
            <Typography variant="h3" color={getConfidenceColor(currentAdvancedScore.confidenceLevel) as any}>
              {currentAdvancedScore.confidenceLevel}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('advancedRisk.dataQualityScore')}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <TrendingUp color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">{t('advancedRisk.interestRate')}</Typography>
            </Box>
            <Typography variant="h3" color="primary">
              {currentPricingResult.finalRate.toFixed(2)}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('advancedRisk.riskBasedPricing')}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Fraud Detection Results */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">
            {t('advancedRisk.fraudDetectionAnalysis')}
          </Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          <Alert 
            severity={currentFraudResult.isFraudulent ? 'error' : 'success'}
            sx={{ mb: 2 }}
          >
            {currentFraudResult.isFraudulent 
              ? t('advancedRisk.fraudulentActivity')
              : t('advancedRisk.noFraudDetected')
            }
          </Alert>

          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Chip
              icon={<Security />}
              label={`${t('advancedRisk.riskLevel')}: ${currentFraudResult.riskLevel}`}
              color={getRiskColor(currentFraudResult.riskLevel) as any}
            />
            <Chip
              icon={<Assessment />}
              label={`${t('advancedRisk.confidence')}: ${currentFraudResult.confidence}%`}
              color={getConfidenceColor(currentFraudResult.confidence) as any}
            />
            <Chip
              icon={<CheckCircle />}
              label={`${t('advancedRisk.action')}: ${currentFraudResult.recommendedAction}`}
              color={currentFraudResult.recommendedAction === 'approve' ? 'success' : 'warning'}
            />
          </Box>

          <Typography variant="subtitle2" gutterBottom>
            {t('advancedRisk.fraudIndicators')} ({currentFraudResult.fraudIndicators.length})
          </Typography>
          {currentFraudResult.fraudIndicators.map((indicator, index) => (
            <Box key={index} sx={{ mb: 1, p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" fontWeight="bold">
                  {indicator.description}
                </Typography>
                <Chip
                  label={indicator.severity}
                  color={getRiskColor(indicator.severity) as any}
                  size="small"
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Evidence: {indicator.evidence}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Weight: {indicator.weight}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Risk Factors */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">
            Risk Factors Analysis
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Risk Factor</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Impact</TableCell>
                <TableCell>Probability</TableCell>
                <TableCell>Mitigation</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentAdvancedScore.riskFactors.map((factor, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {factor.factor}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={factor.category}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={factor.impact}
                      color={getRiskColor(factor.impact) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {(factor.probability * 100).toFixed(0)}%
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {factor.mitigation}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Pricing Analysis */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">
            Risk-Based Pricing Analysis
          </Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Chip
              label={`Base Rate: ${currentPricingResult.baseRate}%`}
              variant="outlined"
            />
            <Chip
              label={`Risk Adjustment: +${currentPricingResult.riskAdjustment.toFixed(2)}%`}
              color="warning"
            />
            <Chip
              label={`Market Adjustment: ${currentPricingResult.marketAdjustment >= 0 ? '+' : ''}${currentPricingResult.marketAdjustment.toFixed(2)}%`}
              color="info"
            />
            <Chip
              label={`Final Rate: ${currentPricingResult.finalRate.toFixed(2)}%`}
              color="primary"
              variant="filled"
            />
          </Box>

                      <Typography variant="subtitle2" gutterBottom>
              Pricing Tiers
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tier</TableCell>
                    <TableCell>Score Range</TableCell>
                    <TableCell>Base Rate</TableCell>
                    <TableCell>Max Amount</TableCell>
                    <TableCell>Max Term</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentPricingResult.pricingTiers && currentPricingResult.pricingTiers.length > 0 ? (
                    currentPricingResult.pricingTiers.map((tier: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Chip label={tier.name} size="small" />
                        </TableCell>
                        <TableCell>{tier.minScore}-{tier.maxScore}</TableCell>
                        <TableCell>{tier.baseRate}%</TableCell>
                        <TableCell>{(tier.maxLoanAmount / 1000000).toFixed(1)}M ZiG</TableCell>
                        <TableCell>{tier.maxLoanTerm} months</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No pricing tiers available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
        </Box>
      </Paper>

      {/* Alternative Pricing Options */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">
            Alternative Pricing Options
          </Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Recommended Rate: {pricingRecommendations.recommendedRate.toFixed(2)}%
          </Typography>
          
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Alternative Terms
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Term (months)</TableCell>
                  <TableCell>Interest Rate</TableCell>
                  <TableCell>Monthly Payment</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pricingRecommendations.alternativeRates.map((option, index) => (
                  <TableRow key={index}>
                    <TableCell>{option.term}</TableCell>
                    <TableCell>{option.rate.toFixed(2)}%</TableCell>
                    <TableCell>{(option.monthlyPayment / 1000).toFixed(0)}K ZiG</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Risk Mitigation Recommendations
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {pricingRecommendations.riskMitigation.map((recommendation, index) => (
              <Chip
                key={index}
                label={recommendation}
                size="small"
                variant="outlined"
                color="info"
              />
            ))}
          </Box>
        </Box>
      </Paper>

      {/* Portfolio Risk Summary */}
      {portfolioRisk && (
        <Paper sx={{ mb: 3 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6">
              Portfolio Risk Summary
            </Typography>
          </Box>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Chip
                label={`Total Loans: ${portfolioRisk.totalLoans}`}
                variant="outlined"
              />
              <Chip
                label={`Avg Credit Score: ${portfolioRisk.averageCreditScore.toFixed(0)}`}
                color="primary"
              />
              <Chip
                label={`Payment Reliability: ${portfolioRisk.paymentMetrics.paymentReliability.toFixed(1)}%`}
                color={portfolioRisk.paymentMetrics.paymentReliability >= 90 ? 'success' : portfolioRisk.paymentMetrics.paymentReliability >= 75 ? 'warning' : 'error'}
              />
            </Box>

            <Typography variant="subtitle2" gutterBottom>
              Risk Distribution
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Chip
                label={`Low Risk: ${portfolioRisk.riskDistribution.low} (${portfolioRisk.riskPercentages.low.toFixed(1)}%)`}
                color="success"
                variant="outlined"
              />
              <Chip
                label={`Medium Risk: ${portfolioRisk.riskDistribution.medium} (${portfolioRisk.riskPercentages.medium.toFixed(1)}%)`}
                color="warning"
                variant="outlined"
              />
              <Chip
                label={`High Risk: ${portfolioRisk.riskDistribution.high} (${portfolioRisk.riskPercentages.high.toFixed(1)}%)`}
                color="error"
                variant="outlined"
              />
            </Box>

            <Typography variant="subtitle2" gutterBottom>
              Payment Metrics
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip
                label={`Completed: ${portfolioRisk.paymentMetrics.completedRepayments}`}
                color="success"
                size="small"
              />
              <Chip
                label={`Overdue: ${portfolioRisk.paymentMetrics.overdueRepayments}`}
                color="error"
                size="small"
              />
              <Chip
                label={`Total: ${portfolioRisk.paymentMetrics.totalRepayments}`}
                variant="outlined"
                size="small"
              />
            </Box>
          </Box>
        </Paper>
      )}

      {/* Recommended Actions */}
      <Paper>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">
            Recommended Actions
          </Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {currentAdvancedScore.recommendedActions && currentAdvancedScore.recommendedActions.length > 0 ? (
              currentAdvancedScore.recommendedActions.map((action, index) => (
                <Chip
                  key={index}
                  label={action}
                  size="small"
                  color="primary"
                  variant="filled"
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No specific actions required at this time.
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default AdvancedRiskDashboard;
